/**
 * `JournalSegment.publicText` backport -- Discord export parsing (PURE module).
 *
 * ## Why this exists
 *
 * The dream entries posted in the Discord thread ("Venryx's dream logs (+PR attempt)") are the
 * canonical "public" form of each dream, but they were never copied into the website's database.
 * `JournalSegment.publicText` (added for Echo Hunt -- see `Source/EchoHunt/Bullets.ts`) is where they
 * belong. This module turns a Discrub JSON export of that thread into a list of candidate
 * `publicText` values, one per dream, plus a *suggested* date for each so the review UI
 * (`UI/Journal/Importer.tsx`, the Journal page's "Importer" tab) can propose which segment it belongs to.
 *
 * NOTHING here touches the DB, the store, or React -- it is a pure function of the export JSON. The
 * whole import is therefore testable in the Node lane (`DiscordExport.test.ts`, using a real
 * excerpt in `__fixtures__/`), and the "which segment?" decision is a separate pure module
 * (`Match.ts`) so it can be reasoned about (and corrected by the user) in isolation.
 *
 * ## The post format (inferred from the 2024-07 → 2026-10 export)
 *
 * ```
 * One lucid this morning:                    <- PREAMBLE (own message, meta-comment, no heading)
 * (the first half of the graph is a mess...)  <-   ...can span several messages
 *
 * # Lucid                                    <- LEVEL-1 HEADING: starts a dream "group"
 *
 * ### Leaving in car; sister gets out...      <- LEVEL-2+ HEADING: a section title
 * * Next thing I recall is being in a car...  <- BULLET: one event/step
 *   * an indented child bullet                <-   ...belongs to the bullet above
 *
 * .                                          <- CONTINUATION MARKER (see below)
 * more of the previous message's body...
 * ```
 *
 * Rules actually observed in the export (verified against all 550 Venryx posting bursts, see the
 * README in this folder for the counts):
 *
 * 1. **Only one author.** Every dream group came from the thread owner; other users' messages are
 *    ignored (`authorNames`, default `["venryx"]`, matched case-insensitively against both
 *    `username` and `global_name`).
 * 2. **A "session" is a burst of consecutive messages from that author**, separated from the next
 *    burst by more than `sessionGapMs` (default 10 minutes). Real bursts are seconds apart; the gap
 *    between separate logging sessions is hours-to-days. Messages from OTHER users in between do not
 *    break a session (the gap is measured between the author's own messages).
 * 3. **A level-1 heading (`# ` at the very start of a message) starts a dream group.** Heading text
 *    is always the first line of the message (0 counter-examples in the export), so a preamble is
 *    always its own message.
 * 4. **Headings name the lucidity kind, optionally with an ordinal**:
 *    `# Lucid`, `# First lucid`, `# Second lucid`, `# Third lucid`, `# Lucid (#2)`,
 *    `# Near-lucid`, `# Semi-lucid`, `# Near-lucid (#1)`, `# Non-lucid`, `# Non-lucid (#2)`.
 *    A heading that names no kind (`# Discussion`) is NOT a dream -- it is reported as skipped.
 *    A trailing parenthetical can also be a DATE instead of an ordinal: `# Lucid (June 1)`.
 * 5. **Everything after a heading, until the next heading, belongs to that group** -- including
 *    prose paragraphs/block-quotes that are not bullets (they are "context" per the Echo Hunt
 *    format notes, but they are part of what was posted, so they are kept verbatim).
 * 6. **A `---` separator line ends the current group** and starts a new preamble. Used when several
 *    backlogged dreams are dumped in one session (each with its own dated preamble).
 * 7. **A message whose first line is exactly `.` is a continuation** of the previous message: the
 *    `.` is Discord-paging noise the author types so the two messages read as one, and it is
 *    stripped. 233 such messages exist in the export.
 * 8. **Preamble text is meta, not dream content** ("One lucid this morning: (+1 near-lucid)"), so it
 *    is kept OUT of `publicText` by default. It is still returned (used for date inference and shown
 *    in the review UI, where it can be opted into per draft).
 *
 * ## Dates
 *
 * The dream's date is the date of the *morning* the dream ended on -- which is what the author means
 * by "the morning of September 11th", and how the DB's wake timestamps read. Sources, in priority
 * order:
 *
 *  1. an explicit date in the preamble (`on the morning of September 11th`, `July 25`),
 *  2. an explicit date in the heading (`# Lucid (June 1)`),
 *  3. a relative word in the preamble (`yesterday morning`, `this morning`),
 *  4. the message timestamp (shifted back a day when posted before 05:00 local, since "this
 *     morning" cannot refer to a morning that has not happened yet).
 *
 * Only 14 of the 99 heading-groups have an explicit date, so (4) carries most of the work; the
 * matched *entry* is displayed (and overridable) in the review UI for exactly that reason.
 *
 * All date math takes an explicit `utcOffsetMinutes` (minutes EAST of UTC, ie. `+420` for UTC+7)
 * rather than using the machine timezone, so results are deterministic in tests and identical for
 * the DB side (which is also formatted with the same offset).
 */

// constants
// ==========

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_AUTHOR_NAMES = ["venryx"];
/** Max gap between two consecutive author messages for them to count as one logging session/burst.
 * Real bursts are seconds apart; separate sessions are hours-to-days apart, so the value is not
 * sensitive -- it only has to sit between "seconds" and "hours". */
export const DEFAULT_SESSION_GAP_MS = 10 * MS_PER_MINUTE;
/** How soon after a group's last message the next posting session must start for its fragment-opening
 * message to count as the same dream. Legit intra-dream gaps peak at ~5 min in the export and off-topic
 * follow-ups start at ~48 min, so a couple of hours is still worth flagging for review. */
export const TRUNCATION_PROXIMITY_MS = 2 * 60 * MS_PER_MINUTE;
/** A post made before this local hour is assumed to describe the PREVIOUS morning (§"Dates" rule 4). */
export const POST_ASSUMED_NEXT_DAY_HOUR_CUTOFF = 5;
/** A body/preamble shorter than this carries no dream content worth importing or reporting. */
export const MIN_MEANINGFUL_DREAM_CHARS = 40;
/** How much of a skipped/un-headed post the review UI shows as its one-line preview. */
export const PREVIEW_MAX_CHARS = 160;

/** A `Month D` written without a year is taken to be in the past, so a later-year match costs this much. */
const YEAR_PENALTY_DAYS = 365;
const MAX_DAYS_IN_MONTH = 31;
/** Markdown horizontal rule: this many of the same character on an otherwise empty line. */
const HORIZONTAL_RULE_MIN_CHARS = 3;
const HORIZONTAL_RULE_REGEXES = ["-", "*", "_"].map(char=>new RegExp(`^\\${char}{${HORIZONTAL_RULE_MIN_CHARS},}$`));
/** Heading levels that count as a "section title": `##` through `######`. (`#` is the dream group.) */
const SECTION_HEADING_LEVELS = "{2,6}";
const SECTION_HEADING_REGEX = new RegExp(`^#${SECTION_HEADING_LEVELS}\\s+\\S`);
const SECTION_HEADING_PREFIX_REGEX = new RegExp(`^#${SECTION_HEADING_LEVELS}\\s+`);

const CONTINUATION_MARKER = ".";
const MONTH_NAMES = [
	"january", "february", "march", "april", "may", "june",
	"july", "august", "september", "october", "november", "december",
] as const;
const ORDINAL_WORDS: Record<string, number> = {
	first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8,
};

// input shapes
// ==========

/** The subset of a Discrub message this module reads. Extra fields (mentions, embeds, reactions, ...)
 * are ignored; only `id`/`timestamp`/`content`/`author` matter here. */
export type DiscordExportMessage = {
	id: string;
	/** ISO-8601, eg. `2026-09-13T00:18:23.457000+00:00` */
	timestamp: string;
	content?: string|n;
	author?: {
		id?: string;
		username?: string;
		global_name?: string|n;
		nick?: string|n;
	};
};

export type DiscordExportFile = DiscordExportMessage[] | {messages?: DiscordExportMessage[]};

// output shapes
// ==========

export const DreamGroupKind_values = ["lucid", "nearLucid", "nonLucid"] as const;
export type DreamGroupKind = typeof DreamGroupKind_values[number];

/** Where a group's `dreamDate` came from -- shown in the review UI, because a date taken from the
 * post timestamp is much weaker evidence than one the author wrote out. */
export const DateSource_values = ["preamble", "heading", "relative", "postTimestamp"] as const;
export type DateSource = typeof DateSource_values[number];

export type DreamDate = {year: number, month: number, day: number};

/** One dream, ie. one candidate `publicText`. */
export type DreamGroup = {
	/** stable id: the Discord id of the message that carried the `# ` heading */
	groupId: string;
	/** the level-1 heading line verbatim, eg. `# First lucid` (also the first line of `publicText`) */
	heading: string;
	kind: DreamGroupKind;
	/** 1-based index of this group among the groups of the same `kind` **on the same night** (a single
	 * posting session can dump a backlog spanning several nights, and each of those nights numbers its
	 * own dreams from 1 -- see `GetNightKey`) */
	ordinal: number;
	/** whether `ordinal` was written by the author, or assigned from the group's position within its night */
	ordinalSource: "explicit"|"order"|"single";

	/** the morning date the dream ended on; null only if the post timestamp was unparseable */
	dreamDate: DreamDate|n;
	dreamDateSource: DateSource|n;

	/** epoch ms of the heading message (used for display and as the date fallback) */
	postTimestamp: number;
	/** the meta-comment before the heading ("One lucid this morning: (+1 near-lucid)"); NOT in `publicText` */
	preamble: string;

	/** the dream report, verbatim markdown, starting with `heading` */
	publicText: string;

	/** `### ...` titles in order (empty for the occasional "unsectioned summary" post) */
	sectionTitles: string[];
	bulletCount: number;
	wordCount: number;
	charCount: number;

	messageIds: string[];
	/** non-fatal observations, surfaced in the review UI so a malformed post is visible rather than silently mis-imported */
	warnings: GroupWarning[];
};

export const GroupWarning_values = [
	"noSections", // no `### ` lines at all -- an "unsectioned summary" post (still importable)
	"noBullets", // no `* ` lines -- prose only
	"tinyBody", // < MIN_MEANINGFUL_DREAM_CHARS of body
	"preambleHasBullets", // the preamble itself has bullets: may be an unsectioned summary ABOVE the heading
	"dateFromPostTimestamp", // no explicit date was written out
	"dateUnknown", // the timestamp could not be parsed
	"possibleTruncation", // the NEXT posting session opens with a fragment (`###`, `.`, a bullet), so this
	// dream's body may have been cut off by the session-gap boundary (see `TRUNCATION_PROXIMITY_MS`)
] as const;
export type GroupWarning = typeof GroupWarning_values[number];

export const SkippedLogReason_values = [
	"headingNotADream", // eg. `# Discussion`
	"noHeading", // a session with dream-ish content but no `# ` heading (older/looser posts)
	"emptyBody", // a heading with nothing after it
] as const;
export type SkippedLogReason = typeof SkippedLogReason_values[number];

/** A session (or part of one) that produced no importable group. Listed in the UI so "we skipped 40
 * things" is visible rather than mysterious. */
export type SkippedLog = {
	reason: SkippedLogReason;
	firstMessageId: string;
	timestamp: number;
	/** first line of the skipped content, truncated */
	preview: string;
	/** the `# ` heading line, when there was one */
	heading?: string;
};

export type ParseDiscordExportResult = {
	groups: DreamGroup[];
	skipped: SkippedLog[];
	stats: {
		messagesRead: number;
		duplicatesRemoved: number;
		messagesFromAuthor: number;
		sessions: number;
		/** author messages dropped because they were posted before `options.minPostDate` (not parsed at all) */
		messagesBeforeMinDate: number;
	};
};

export type ParseDiscordExportOptions = {
	/** matched case-insensitively against `username`/`global_name`; defaults to `DEFAULT_AUTHOR_NAMES` */
	authorNames?: string[];
	sessionGapMs?: number;
	/** minutes EAST of UTC, eg. `+420` for UTC+7. Defaults to 0. */
	utcOffsetMinutes?: number;
	/** Posts (author messages) whose local date is BEFORE this date are discarded before any grouping
	 * or parsing happens, so they produce neither drafts nor skipped-log entries. */
	minPostDate?: DreamDate|n;
};

// date helpers
// ==========

/** Wall-clock date/time parts at a fixed UTC offset (deterministic -- no machine timezone involved). */
export function GetLocalDateParts(epochMs: number, utcOffsetMinutes: number) {
	const shifted = new Date(epochMs + utcOffsetMinutes * MS_PER_MINUTE);
	return {
		year: shifted.getUTCFullYear(),
		month: shifted.getUTCMonth() + 1,
		day: shifted.getUTCDate(),
		hour: shifted.getUTCHours(),
		minute: shifted.getUTCMinutes(),
	};
}

/** Stable integer for a calendar date, so date differences do not need `Date` arithmetic. */
export function ToDayNumber(date: DreamDate): number {
	return Math.round(Date.UTC(date.year, date.month - 1, date.day) / MS_PER_DAY);
}

export function FormatDreamDate(date: DreamDate|n): string {
	if (date == null) return "(unknown date)";
	return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

/** Parses a `YYYY-MM-DD` (or `YYYY-M-D`) string into a `DreamDate`; null if it isn't a real date. */
export function ParseDreamDateString(text: string): DreamDate|n {
	const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text.trim());
	if (match == null) return null;
	const year = parseInt(match[1], 10);
	const month = parseInt(match[2], 10);
	const day = parseInt(match[3], 10);
	if (month < 1 || month > 12 || day < 1 || day > MAX_DAYS_IN_MONTH) return null;
	// reject days the month does not actually have (eg. 2026-02-30)
	const roundTripped = DateFromDayNumber(ToDayNumber({year, month, day}));
	if (roundTripped.year != year || roundTripped.month != month || roundTripped.day != day) return null;
	return {year, month, day};
}

export function DateFromDayNumber(dayNumber: number): DreamDate {
	const d = new Date(dayNumber * MS_PER_DAY);
	return {year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate()};
}

export function AddDays(date: DreamDate, days: number): DreamDate {
	return DateFromDayNumber(ToDayNumber(date) + days);
}

/** Pulls a `Month D` (or `Month Dth`) date out of free text. Year is deliberately not parsed: the
 * posts never write one, and a stale year is worse than the "nearest past year" inference. */
export function ParseExplicitMonthDay(text: string): {month: number, day: number}|null {
	if (!text) return null;
	const monthAlternation = MONTH_NAMES.join("|");
	const match = new RegExp(`\\b(${monthAlternation})[a-z]*\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, "i").exec(text);
	if (match == null) return null;
	const month = MONTH_NAMES.indexOf(match[1].toLowerCase() as typeof MONTH_NAMES[number]) + 1;
	const day = parseInt(match[2], 10);
	if (month < 1 || day < 1 || day > MAX_DAYS_IN_MONTH) return null;
	return {month, day};
}

/** Picks the year for a `Month D` that was written without one: the year that puts the date at (or
 * just before) the post date. Handles the "logged a backlog from last year" case. */
export function InferYearForMonthDay(monthDay: {month: number, day: number}, postLocal: {year: number, month: number, day: number}): DreamDate {
	const postDayNumber = ToDayNumber({year: postLocal.year, month: postLocal.month, day: postLocal.day});
	let best: DreamDate|n = null;
	let bestDiff = Number.POSITIVE_INFINITY;
	for (const year of [postLocal.year, postLocal.year - 1, postLocal.year + 1]) {
		const candidate = {year, month: monthDay.month, day: monthDay.day};
		const diff = ToDayNumber(candidate) - postDayNumber;
		// prefer a date at/just before the post; anything far past it is a later year's typo
		const weight = diff >= 0 ? diff + YEAR_PENALTY_DAYS : -diff;
		if (weight < bestDiff) {
			bestDiff = weight;
			best = candidate;
		}
	}
	return best!;
}

// text helpers
// ==========

function NormalizeNewlines(text: string) {
	return text.replace(/\r\n?/g, "\n");
}

function IsHorizontalRule(line: string) {
	const trimmed = line.trim();
	if (trimmed.length < HORIZONTAL_RULE_MIN_CHARS) return false;
	return HORIZONTAL_RULE_REGEXES.some(regex=>regex.test(trimmed));
}

function CountBullets(lines: string[]) {
	let count = 0;
	for (const line of lines) {
		if (/^\s*[*+-]\s+\S/.test(line)) count++;
	}
	return count;
}

function CountWords(text: string) {
	return text.split(/\s+/).filter(a=>a.length > 0).length;
}

function TrimBlankLinesAtEdges(lines: string[]) {
	let start = 0;
	let end = lines.length;
	while (start < end && lines[start].trim().length == 0) start++;
	while (end > start && lines[end - 1].trim().length == 0) end--;
	return lines.slice(start, end);
}

/** Blank line above every `### `-style heading: the Discord posts are written tight, which reads as a
 * wall of text once the markdown is rendered elsewhere (eg. in the journal UI / Echo Hunt views). */
function EnsureBlankLineBeforeSectionHeadings(lines: string[]) {
	const result: string[] = [];
	for (const line of lines) {
		const previousLine = result.length > 0 ? result[result.length - 1] : null;
		if (/^#{2,6}\s+\S/.test(line) && previousLine != null && previousLine.trim().length > 0) result.push("");
		result.push(line);
	}
	return result;
}

/** The first non-blank line of a message, kept RAW (a leading `.` is itself a fragment signature here,
 * so it must not be stripped as rule 7 does for body assembly). */
function GetFirstLine(message: DiscordExportMessage) {
	const lines = NormalizeNewlines(message.content ?? "").split("\n");
	return lines.find(a=>a.trim().length > 0) ?? "";
}

/** A line that cannot begin a standalone post, so a session STARTING with one means the content lives
 * in an EARLIER session. Over the whole export, 0 sessions legitimately start with a `.` or a bullet.
 * (`##`+ headings are safe too: only a `# ` heading starts a dream.) */
function IsFragmentFirstLine(line: string) {
	const trimmed = line.trim();
	if (trimmed.length == 0) return false;
	if (trimmed == CONTINUATION_MARKER) return true;
	if (trimmed.startsWith(`${CONTINUATION_MARKER} `)) return true;
	if (SECTION_HEADING_REGEX.test(trimmed)) return true;
	return /^[*+-]\s+\S/.test(trimmed);
}

// heading parsing
// ==========

export type ParsedHeading = {
	kind: DreamGroupKind|n;
	ordinal: number|n;
	dateHint: {month: number, day: number}|n;
};

/** Parses a `# ...` heading line into its kind / ordinal / date hint. `kind == null` means "not a
 * dream" (`# Discussion`), which the caller reports as skipped. */
export function ParseDreamHeading(headingLine: string): ParsedHeading {
	const withoutHashes = headingLine.replace(/^#+\s*/, "").trim();

	// parentheticals carry either an ordinal (`# Non-lucid (#1)`) or a date (`# Lucid (June 1)`)
	const parentheticals = [...withoutHashes.matchAll(/\(([^)]*)\)/g)].map(a=>a[1].trim());
	const core = withoutHashes.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

	// the ordinal, when written, comes FIRST: "First lucid", "Second lucid", "1st lucid"
	const words = core.split(" ").filter(a=>a.length > 0);
	let ordinal: number|n = null;
	if (words.length > 1) {
		ordinal = ParseOrdinalWord(words[0]);
		if (ordinal != null) words.shift();
	}
	const rest = words.join(" ");

	let kind: DreamGroupKind|n = null;
	if (/^non[\s-]?lucid/.test(rest)) kind = "nonLucid";
	else if (/^(near|semi)[\s-]?lucid/.test(rest)) kind = "nearLucid";
	else if (rest.startsWith("lucid")) kind = "lucid";

	let dateHint: {month: number, day: number}|n = null;
	for (const parenthetical of parentheticals) {
		const ordinalMatch = /^#\s*(\d+)$/.exec(parenthetical);
		if (ordinalMatch) {
			ordinal = parseInt(ordinalMatch[1], 10);
			continue;
		}
		const monthDay = ParseExplicitMonthDay(parenthetical);
		if (monthDay != null) {
			dateHint = monthDay;
			continue;
		}
	}

	return {kind, ordinal, dateHint};
}

function ParseOrdinalWord(word: string): number|n {
	if (ORDINAL_WORDS[word] != null) return ORDINAL_WORDS[word];
	const numeric = /^(\d+)(?:st|nd|rd|th)$/.exec(word);
	if (numeric) return parseInt(numeric[1], 10);
	return null;
}

// loading / grouping
// ==========

/** Flattens one-or-more Discrub export files into a deduped, id-ordered list.
 * Page boundaries cut through the middle of dreams (a heading can end one page while its sections
 * start the next), so every page must be merged before anything is grouped. */
export function MergeDiscordExports(files: DiscordExportFile[]) {
	const seen = new Map<string, DiscordExportMessage>();
	let messagesRead = 0;
	for (const file of files) {
		const messages = Array.isArray(file) ? file : (file?.messages ?? []);
		for (const message of messages) {
			if (message?.id == null) continue;
			messagesRead++;
			seen.set(String(message.id), message);
		}
	}
	const messages = [...seen.values()];
	// Discord snowflake ids are monotonically increasing, so sorting by them is exact even when two
	// messages share a millisecond -- and it avoids depending on the `timestamp` string format
	messages.sort((a,b)=>CompareSnowflakes(a.id, b.id));
	return {messages, stats: {messagesRead, duplicatesRemoved: messagesRead - messages.length}};
}

function CompareSnowflakes(a: string, b: string) {
	if (a.length != b.length) return a.length - b.length;
	return a < b ? -1 : a > b ? 1 : 0;
}

export function MessageAuthorNames(message: DiscordExportMessage): string[] {
	const result: string[] = [];
	if (message.author?.username) result.push(message.author.username);
	if (message.author?.global_name) result.push(message.author.global_name);
	if (message.author?.nick) result.push(message.author.nick);
	return result.map(a=>a.toLowerCase());
}

export function IsFromAuthor(message: DiscordExportMessage, authorNames: string[]) {
	const names = MessageAuthorNames(message);
	const wanted = authorNames.map(a=>a.toLowerCase());
	return names.some(name=>wanted.includes(name));
}

/** Splits the author's messages into "sessions" (bursts). The gap is measured between CONSECUTIVE
 * AUTHOR messages, so a reply from another user in between does not split a session. */
export function GroupMessagesIntoSessions(messages: DiscordExportMessage[], sessionGapMs = DEFAULT_SESSION_GAP_MS) {
	const sessions: DiscordExportMessage[][] = [];
	let current: DiscordExportMessage[]|n = null;
	let previousTimestamp: number|n = null;
	for (const message of messages) {
		const timestamp = Date.parse(message.timestamp);
		const startsNewSession = current == null || previousTimestamp == null || !(timestamp - previousTimestamp <= sessionGapMs);
		if (startsNewSession) {
			current = [];
			sessions.push(current);
		}
		current!.push(message);
		previousTimestamp = timestamp;
	}
	return sessions;
}

// parsing
// ==========

/** Parses every export file into dream groups (one per dream) plus the logs that were skipped. */
export function ParseDiscordExport(files: DiscordExportFile[], options: ParseDiscordExportOptions = {}): ParseDiscordExportResult {
	const authorNames = options.authorNames ?? DEFAULT_AUTHOR_NAMES;
	const sessionGapMs = options.sessionGapMs ?? DEFAULT_SESSION_GAP_MS;
	const utcOffsetMinutes = options.utcOffsetMinutes ?? 0;
	const minPostDate = options.minPostDate ?? null;
	const minPostDayNumber = minPostDate == null ? null : ToDayNumber(minPostDate);

	const merged = MergeDiscordExports(files);
	const authored_all = merged.messages.filter(a=>IsFromAuthor(a, authorNames));
	// Drop posts from before the min-date BEFORE any grouping, so an out-of-range post is not merely
	// "skipped" -- it never becomes a draft, a skipped-log entry, or part of a posting session.
	let authored = authored_all;
	let messagesBeforeMinDate = 0;
	if (minPostDayNumber != null) {
		authored = authored_all.filter(message=>{
			const timestamp = Date.parse(message.timestamp);
			// an unparseable timestamp cannot be dated, so treat it as in-range rather than silently dropping it
			if (Number.isNaN(timestamp)) return true;
			const local = GetLocalDateParts(timestamp, utcOffsetMinutes);
			const inRange = ToDayNumber({year: local.year, month: local.month, day: local.day}) >= minPostDayNumber;
			if (!inRange) messagesBeforeMinDate++;
			return inRange;
		});
	}
	const sessions = GroupMessagesIntoSessions(authored, sessionGapMs);

	const groups: DreamGroup[] = [];
	const skipped: SkippedLog[] = [];
	const openGroupIdPerSession: Array<string|n> = [];
	for (const session of sessions) {
		openGroupIdPerSession.push(ParseSession(session, utcOffsetMinutes, groups, skipped));
	}
	MarkPossibleTruncations(sessions, openGroupIdPerSession, groups);

	return {
		groups,
		skipped,
		stats: {
			messagesRead: merged.stats.messagesRead,
			duplicatesRemoved: merged.stats.duplicatesRemoved,
			messagesFromAuthor: authored.length,
			sessions: sessions.length,
			messagesBeforeMinDate,
		},
	};
}

type GroupInProgress = {
	heading: string;
	headingMessage: DiscordExportMessage;
	dateHint: {month: number, day: number}|n;
	ordinalExplicit: number|n;
	preambleLines: string[];
	bodyLines: string[];
	messageIds: string[];
};

/** Everything about a group that does NOT depend on its ordinal. Resolved in a first pass, because
 * ordinals are numbered per (kind, night) and therefore need every group of the session dated first. */
type GroupBasics = {
	kind: DreamGroupKind;
	postTimestamp: number;
	dateInfo: {date: DreamDate|n, source: DateSource|n};
};

/** Identity of the "night" a group belongs to, for ordinal numbering. Groups whose night could not be
 * determined share one pseudo-night (there is nothing better to go on, and it keeps them from all
 * claiming to be the first dream of their kind). */
function GetNightKey(kind: DreamGroupKind, dateInfo: {date: DreamDate|n}) {
	return `${kind}|${dateInfo.date == null ? "unknown" : ToDayNumber(dateInfo.date)}`;
}

function ParseSession(session: DiscordExportMessage[], utcOffsetMinutes: number, groupsOut: DreamGroup[], skippedOut: SkippedLog[]) {
	const groupsInProgress: GroupInProgress[] = [];
	let current: GroupInProgress|n = null;
	let preambleLines: string[] = [];
	/** set after a non-dream heading (`# Discussion`), so that heading's body does not become the
	 *  NEXT group's preamble (and poison its date inference) */
	let swallowUntilBoundary = false;

	const closeCurrent = ()=>{
		if (current != null) groupsInProgress.push(current);
		current = null;
	};

	for (const message of session) {
		const content = NormalizeNewlines(message.content ?? "");
		if (content.trim().length == 0) {
			// blank messages carry no structure, but DO keep the session's line breaks tidy
			if (current != null) current.bodyLines.push("");
			else preambleLines.push("");
			continue;
		}

		const lines = content.split("\n");
		// rule 7: a leading `.` line is Discord-paging noise, not content
		if (lines[0].trim() == CONTINUATION_MARKER) lines.shift();
		else if (lines[0].trim().startsWith(`${CONTINUATION_MARKER} `)) lines[0] = lines[0].trim().slice(1).trimStart();

		const firstContentIndex = lines.findIndex(a=>a.trim().length > 0);
		const firstContentLine = firstContentIndex == -1 ? "" : lines[firstContentIndex];

		// rule 6: `---` ends the current group and starts a fresh preamble
		if (IsHorizontalRule(firstContentLine)) {
			closeCurrent();
			preambleLines = lines.slice(firstContentIndex + 1);
			swallowUntilBoundary = false;
			continue;
		}

		// rule 3/4: a level-1 heading starts a new group
		if (/^#\s+\S/.test(firstContentLine)) {
			closeCurrent();
			const heading = firstContentLine.trim();
			const parsed = ParseDreamHeading(heading);
			if (parsed.kind == null) {
				// `# Discussion` and friends: not a dream, but the body must not leak into the next group
				skippedOut.push({
					reason: "headingNotADream",
					firstMessageId: message.id,
					timestamp: Date.parse(message.timestamp),
					preview: Truncate(lines.slice(firstContentIndex + 1).join(" ").trim(), PREVIEW_MAX_CHARS),
					heading,
				});
				preambleLines = [];
				swallowUntilBoundary = true;
				continue;
			}
			current = {
				heading,
				headingMessage: message,
				dateHint: parsed.dateHint,
				ordinalExplicit: parsed.ordinal,
				preambleLines: [...preambleLines],
				bodyLines: lines.slice(firstContentIndex + 1),
				messageIds: [message.id],
			};
			preambleLines = [];
			swallowUntilBoundary = false;
			continue;
		}

		if (swallowUntilBoundary) continue;

		// rule 5: everything else extends the open group, or the pending preamble
		if (current != null) {
			current.bodyLines.push(...lines);
			current.messageIds.push(message.id);
		} else {
			preambleLines.push(...lines);
		}
	}
	// the group still receiving messages when the session ran out (null if it was closed by `---` etc.)
	const openAtEndGroupId = current?.headingMessage.id ?? null;
	closeCurrent();

	if (groupsInProgress.length == 0) {
		const preambleText = TrimBlankLinesAtEdges(preambleLines).join("\n").trim();
		if (preambleText.length >= MIN_MEANINGFUL_DREAM_CHARS) {
			skippedOut.push({
				reason: "noHeading",
				firstMessageId: session[0].id,
				timestamp: Date.parse(session[0].timestamp),
					preview: Truncate(preambleText.replace(/\n/g, " "), PREVIEW_MAX_CHARS),
			});
		}
		return null;
	}

	// resolve ordinals per kind AND NIGHT (rule 4): explicit > order-of-appearance > "single".
	// Per NIGHT, not per session: one burst often dumps a backlog of several nights, each numbering its
	// own dreams from 1. See `GetNightKey`.
	const resolved: Array<{group: GroupInProgress, basics: GroupBasics}> = [];
	for (const group of groupsInProgress) {
		const basics = ResolveGroupBasics(group, utcOffsetMinutes);
		if (basics == null) continue;
		resolved.push({group, basics});
	}
	const countsByNight = new Map<string, number>();
	for (const {basics} of resolved) {
		const nightKey = GetNightKey(basics.kind, basics.dateInfo);
		countsByNight.set(nightKey, (countsByNight.get(nightKey) ?? 0) + 1);
	}
	const assignedByNight = new Map<string, number>();

	for (const {group, basics} of resolved) {
		const built = BuildGroup(group, basics, countsByNight, assignedByNight);
		if (built == null) continue;
		groupsOut.push(built);
	}
	return openAtEndGroupId;
}

/** Flags a group whose body may have been CUT OFF by a session boundary: the session ended with it
 * still open, and the next session opens with a fragment (`###`/`.`/bullet) soon enough to be the same
 * night. A real split leaves no other trace -- the dropped tail just becomes an ignored post. */
function MarkPossibleTruncations(sessions: DiscordExportMessage[][], openGroupIdPerSession: Array<string|n>, groups: DreamGroup[]) {
	const groupsById = new Map(groups.map(a=>[a.groupId, a]));
	for (let i = 0; i < sessions.length - 1; i++) {
		const openGroupId = openGroupIdPerSession[i];
		if (openGroupId == null) continue;
		const group = groupsById.get(openGroupId);
		if (group == null) continue;

		const nextSession = sessions[i + 1];
		const gapMs = Date.parse(nextSession[0].timestamp) - Date.parse(sessions[i][sessions[i].length - 1].timestamp);
		if (!(gapMs <= TRUNCATION_PROXIMITY_MS)) continue;
		if (!IsFragmentFirstLine(GetFirstLine(nextSession[0]))) continue;

		group.warnings.push("possibleTruncation");
	}
}

/** Kind + night of a group, ie. the parts of a `DreamGroup` that its ordinal does not depend on. */
function ResolveGroupBasics(group: GroupInProgress, utcOffsetMinutes: number): GroupBasics|null {
	const parsed = ParseDreamHeading(group.heading);
	if (parsed.kind == null) return null;

	const postTimestamp = Date.parse(group.headingMessage.timestamp);
	const postLocal = Number.isNaN(postTimestamp) ? null : GetLocalDateParts(postTimestamp, utcOffsetMinutes);
	const preamble = TrimBlankLinesAtEdges(group.preambleLines).join("\n").trim();
	const dateInfo = ResolveDreamDate(preamble, group.heading, parsed.dateHint, postTimestamp, postLocal);
	return {kind: parsed.kind, postTimestamp, dateInfo};
}

function BuildGroup(
	group: GroupInProgress,
	basics: GroupBasics,
	countsByNight: Map<string, number>,
	assignedByNight: Map<string, number>,
): DreamGroup|null {
	const {kind, postTimestamp, dateInfo} = basics;

	// the ordinal counts groups of the same kind ON THE SAME NIGHT (see the note in `ParseSession`)
	const nightKey = GetNightKey(kind, dateInfo);
	const nextAssigned = (assignedByNight.get(nightKey) ?? 0) + 1;
	assignedByNight.set(nightKey, nextAssigned);
	const ordinal = group.ordinalExplicit ?? nextAssigned;
	const ordinalSource = group.ordinalExplicit != null ? "explicit" : ((countsByNight.get(nightKey) ?? 0) == 1 ? "single" : "order");

	const bodyLines = TrimBlankLinesAtEdges(group.bodyLines);
	const preambleLines = TrimBlankLinesAtEdges(group.preambleLines);
	const preamble = preambleLines.join("\n").trim();
	const publicText = TrimBlankLinesAtEdges(EnsureBlankLineBeforeSectionHeadings([group.heading, ...bodyLines])).join("\n").trim();

	const sectionTitles = bodyLines
		.filter(a=>SECTION_HEADING_REGEX.test(a))
		.map(a=>a.replace(SECTION_HEADING_PREFIX_REGEX, "").trim());
	const bulletCount = CountBullets(bodyLines);
	const bodyText = bodyLines.join("\n").trim();

	const warnings: GroupWarning[] = [];
	if (sectionTitles.length == 0) warnings.push("noSections");
	if (bulletCount == 0) warnings.push("noBullets");
	if (bodyText.length < MIN_MEANINGFUL_DREAM_CHARS) warnings.push("tinyBody");
	if (CountBullets(preambleLines) > 0) warnings.push("preambleHasBullets");
	if (dateInfo.source == "postTimestamp") warnings.push("dateFromPostTimestamp");
	if (dateInfo.date == null) warnings.push("dateUnknown");

	return {
		groupId: group.headingMessage.id,
		heading: group.heading,
		kind,
		ordinal,
		ordinalSource,
		dreamDate: dateInfo.date,
		dreamDateSource: dateInfo.source,
		postTimestamp,
		preamble,
		publicText,
		sectionTitles,
		bulletCount,
		wordCount: CountWords(publicText),
		charCount: publicText.length,
		messageIds: group.messageIds,
		warnings,
	};
}

function ResolveDreamDate(
	preamble: string,
	headingLine: string,
	dateHintFromHeading: {month: number, day: number}|n,
	postTimestamp: number,
	postLocal: {year: number, month: number, day: number, hour: number}|n,
): {date: DreamDate|n, source: DateSource|n} {
	if (postLocal == null) return {date: null, source: null};

	const preambleMonthDay = ParseExplicitMonthDay(preamble);
	if (preambleMonthDay) return {date: InferYearForMonthDay(preambleMonthDay, postLocal), source: "preamble"};

	if (dateHintFromHeading) return {date: InferYearForMonthDay(dateHintFromHeading, postLocal), source: "heading"};

	const headingMonthDay = ParseExplicitMonthDay(headingLine);
	if (headingMonthDay) return {date: InferYearForMonthDay(headingMonthDay, postLocal), source: "heading"};

	const postDate = {year: postLocal.year, month: postLocal.month, day: postLocal.day};
	// only "yesterday morning"/"morning of yesterday" shift the date: a bare "yesterday" elsewhere in
	// the preamble ("fell asleep yesterday evening") describes something else entirely
	if (/\b(?:yesterday'?s?\s+morning|morning\s+of\s+yesterday)\b/i.test(preamble)) {
		return {date: AddDays(postDate, -1), source: "relative"};
	}

	const postedBeforeDawn = postLocal.hour < POST_ASSUMED_NEXT_DAY_HOUR_CUTOFF;
	return {date: AddDays(postDate, postedBeforeDawn ? -1 : 0), source: "postTimestamp"};
}

function Truncate(text: string, maxLength: number) {
	if (text.length <= maxLength) return text;
	return `${text.slice(0, maxLength - 1)}…`;
}
